import { ElectronApi } from '@/services/electron.api';
import { GlobalConfig } from '@/services/gloabl.config';
import { IDBService } from '@/services/idb.service';
import { fileToMd5 } from '@/utils/md5.utils';
import { ImageMode } from '../lvImgConv/enums';
import { WatchfaceDeviceType } from '@/hooks';

export const LvglConvert: { [key: string]: number } = {
  argb8888: ImageMode.CF_TRUE_COLOR_ALPHA,
  xrgb8888: ImageMode.CF_TRUE_COLOR,
  alpha8: ImageMode.CF_ALPHA_8_BIT,
  indexed8: ImageMode.CF_INDEXED_8_BIT,
  rgb565: ImageMode.CF_TRUE_COLOR,
  argb8565: ImageMode.CF_TRUE_COLOR_ALPHA
};
const srcToDom = (src: string): Promise<HTMLImageElement> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.src = src;
    img.onload = () => resolve(img);
    img.onerror = () => reject(null);
  });
};

export async function getImageData(
  imgPath: string
): Promise<{ data: Uint8ClampedArray; width: number; height: number }> {
  const fs = ElectronApi.fs;
  if (imgPath && fs) {
    const bitmap = await fs.promises.readFile(imgPath);
    const base64 = `data:image/png;base64,${Buffer.from(bitmap).toString(
      'base64'
    )}`;
    const image = await srcToDom(base64);
    const canvas = document.createElement('canvas');
    canvas.width = image.width;
    canvas.height = image.height;
    const ctx = canvas.getContext('2d', {
      willReadFrequently: true
    });
    if (ctx === null) {
      throw 'Invalid canvas context';
    }

    ctx.drawImage(image, 0, 0);
    const imageData = ctx.getImageData(0, 0, image.width, image.height);
    return {
      data: imageData.data as Uint8ClampedArray,
      width: image.width,
      height: image.height
    };
  } else {
    const canvas = document.createElement('canvas');
    canvas.width = 390;
    canvas.height = 450;
    const ctx = canvas.getContext('2d', {
      willReadFrequently: true
    });
    if (ctx === null) {
      throw 'Invalid canvas context';
    }
    ctx.fillStyle = 'black';
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    return {
      data: imageData.data as Uint8ClampedArray,
      width: canvas.width,
      height: canvas.height
    };
  }
}

export const getCacheKey = async (data: {
  imgPath: string;
  format: string;
  is16bpp: boolean;
}) => {
  try {
    const { imgPath, format, is16bpp } = data;
    const cacheKey = [await fileToMd5(imgPath, false), format, is16bpp].join(
      '_'
    );
    return cacheKey;
  } catch (error) {
    return;
  }
};

export const getIs16bpp = (deviceType: string) => {
  return GlobalConfig['using16bppDeviceTypes'].includes(deviceType);
};

export const imageCahcehIdb = new IDBService(
  'db-cache',
  'stroe-export-image-buffer'
);

/**
 * 是否需要缓存convertData
 * 当is16bpp=true 或者 is16bpp==false&&format==indexed8时才进行缓存
 * @param watchfaceType 可选，如果未提供，则忽略watchfaceType影响
 * @param format 可选，如果未提供，则忽略format的影响
 * @returns
 */
export const needCacheConvertData = (data: {
  watchfaceType?: WatchfaceDeviceType;
  format?: string;
  is16bpp?: boolean;
}) => {
  const { watchfaceType, format = 'indexed8', is16bpp: __is16bpp } = data;
  const isN65AB = watchfaceType
    ? ['N65A', 'N65B', 'N65C'].includes(watchfaceType)
    : false;
  const _is16bpp =
    __is16bpp != undefined
      ? __is16bpp
      : watchfaceType
      ? getIs16bpp(watchfaceType)
      : true;
  return (
    // 不能是N65A/B/C
    !isN65AB &&
    // 全局配置中开启
    GlobalConfig.enablePngConvertCache &&
    // 16bpp或者 !16bpp&&format=indexed8
    (_is16bpp || format == 'indexed8')
  );
};

export const getConvertParams = async (options: {
  imgPath: string;
  is16bpp: boolean;
  format: string;
}) => {
  const { imgPath, format = 'argb8888', is16bpp = true } = options;
  const { data, width, height } = await getImageData(imgPath); //传入图片路径并通过canvas转后的二进制路径
  if (is16bpp) {
    return {
      imgData: data as unknown as Uint8Array,
      width,
      height,
      options: {
        cf: LvglConvert[format],
        binaryFormat: ImageMode.ICF_TRUE_COLOR_ARGB8565
      }
    };
  } else if (format == 'indexed8') {
    const rgbaData: number[][] = [];
    for (let i = 0; i < data.length; i += 4) {
      // [R, G, B, A]
      rgbaData.push([data[i], data[i + 1], data[i + 2], data[i + 3]]);
    }
    return {
      imgData: rgbaData.flat(),
      width,
      height,
      options: {
        cf: ImageMode.CF_INDEXED_8_BIT,
        binaryFormat: ImageMode.CF_INDEXED_8_BIT
      }
    };
  }
};
